import { createContext, memo, useCallback, useEffect, useState } from 'react';
import { getWikiCategoryData } from '@/services/introduction';
import CollapseList from '@/components/CollapseList';
import IntroductionPage from '@/components/IntroductionPage';
import styles from '@/styles/pages/introduction.module.scss';
import { Breadcrumb, ConfigProvider, message } from 'antd';
import Image from 'next/image';
import Rating from '@/components/common/rate-component';
import { useTranslation } from '@/hooks/use-translation';
import { useRouter } from 'next/router';
import Head from 'next/head';
import { BreadcrumbList } from 'schema-dts';
import { jsonLdScriptProps } from 'react-schemaorg';
import { handleShare, shareMediaURl } from '@/utils/mediaShare';
import { useLanguage } from '@/hooks/use-language';
import dayJs from 'dayjs';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { encryption } from '@/utils/encryption';
import http from '@/services/http';
import classNames from 'classnames';

export default memo((props: any) => {
  // const {data} = props;
  // const data = JSON.parse(props?.data);
  const initData = props.data;
  const [isRate, setIsRate] = useState(false);
  const [ratingScore, setRatingScore] = useState(initData?.wiki_category_setup?.blog_rating || 5.0);
  const [shareUrl, setShareUrl] = useState('');
  const [breadcrumbList, setBreadcrumbList] = useState<any[]>([]);
  const t = useTranslation();
  const router = useRouter();

  // const introduction = router.query.introduction as string;
  const luanuageType = useLanguage() === 'zh' ? 'zh-hk' : '';

  const setRateForArticle = async (score: number) => {
    const { url, uid, title } = initData;
    if (!url) return false;

    const fpPromise = FingerprintJS.load();
    const fp = await fpPromise;
    const fpResult = await fp.get();
    const visitorId = fpResult.visitorId;

    const inputText = url + ':' + luanuageType + ':' + score + ':' + fpResult.visitorId;

    const formKey = encryption(inputText);

    const data = {
      pageUrl: url,
      entryUid: uid,
      contentType: 'blog_wiki_category_page',
      rating: score,
      blogTitle: title,
      author: '',
      category: '',
      productTag: '',
      topicTag: '',
      language: luanuageType,
      visitorId,
      formKey
    };
    const result = await http({}).post('/article/rate', data);
    setIsRate(true);
    setRatingScore(score);
    localStorage.setItem(initData.uid, 'true');
    if (result.data) {
      setRatingScore(result.data.rating_score);
    }

    window?.dataLayer?.push({
      event: 'buttonClick',
      buttonTitle: 'rate_article',
      description: score,
      destinationUrl: '',
      pageURL: window.location.href,
      componentName: 'article rating'
    });
  };

  const language = useLanguage();

  const copyLink = () => {
    message.config({
      top: 0,
      duration: 20
    });
    navigator.clipboard
      .writeText(shareUrl)
      .then(() => {
        message.success(t('copy_success'));
      })
      .catch(() => {
        message.error(t('copy_fail'));
      });
  };

  const shareArticle = (event: any, type: string) => {
    window?.dataLayer?.push({
      event: 'buttonClick',
      buttonTitle: 'share_article',
      description: type,
      destinationUrl: '',
      pageURL: shareUrl,
      componentName: 'article share'
    });
    handleShare(event, type);
  };

  // 埋点
  const handleGA = (title: any, href: any) => {
    window?.dataLayer?.push({
      event: 'buttonClick',
      buttonTitle: 'guide_menu_bar',
      description: title,
      destinationUrl: href,
      pageURL: window.location.href,
      componentName: 'breadcrumb'
    });
  };

  useEffect(() => {
    if (typeof window !== 'undefined' && initData) {
      const rateStatus = JSON.parse(localStorage.getItem(initData.uid) || 'false');
      setIsRate(rateStatus);
    }
    setShareUrl(window.location.href);
  }, []);

  // Wiki Article Page BreadCrumb shema
  useEffect(() => {
    let list = [
      {
        name: language == 'zh' ? '首頁' : 'Home',
        link: language == 'zh' ? 'https://www.fwd.com.hk/' : 'https://www.fwd.com.hk/en/'
      },
      {
        name: language == 'zh' ? '網誌' : 'Blog',
        link: window.location.origin + (language == 'zh' ? '/zh/blog/' : '/en/blog/')
      },
      {
        name: language == 'zh' ? '超基本保險教室' : 'Guide',
        link: window.location.origin + (language == 'zh' ? '/zh/blog/guide/' : '/en/blog/guide/')
      },
      {
        name: initData?.title,
        link:
          window.location.origin +
          (language == 'zh' ? `/zh/blog/guide${initData?.url}` : `/en/blog/guide${initData?.url}`)
      }
    ];
    setBreadcrumbList(list);
  }, []);

  // 埋点
  useEffect(() => {
    setTimeout(() => {
      window?.dataLayer?.push({
        event: 'content_landing',
        author: initData?.publish_details?.user || '',
        description:
          initData?.locale == 'zh-hk'
            ? initData?.wiki_category_setup?.description?.substring(0, 20)
            : initData?.wiki_category_setup?.description?.substring(0, 40),
        publish_time: initData?.publish_details?.time,
        last_edit_time: initData?.updated_at,
        subtitle: '',
        tags: '',
        title: initData?.title,
        content_Group:
          initData?.locale == 'zh-hk' ? '文章|' + '超基本保險教室' : 'article|' + 'guide',
        pageURL: window.location.href
      });
    }, 1000);
  }, []);

  return (
    <div className={styles.PC_wrapper}>
      <div className={styles.main_wrapper}>
        <div className={styles.breadcrumb_wrapper}>
          <ConfigProvider
            theme={{
              components: {
                Breadcrumb: {
                  separatorColor: '#183028',
                  linkColor: '#E87722',
                  linkHoverColor: '#E87722',
                },
              },
            }}
          >
            <Breadcrumb
              separator={
                <Image
                  src="/blog/icon/BreadIcon.svg"
                  className={classNames(styles.Bread_Icon, language === 'zh' ? '' : styles.Bread_Icon_EN)}
                  alt={''}
                  width={16}
                  height={16}
                />
              }
              className={classNames(styles.Breadcrumb, language === 'zh' ? styles.Breadcrumb_ZH : styles.Breadcrumb_EN)}
              items={[
                {
                  type: 'separator',
                  separator: (
                    <div
                      onClick={() => {
                        router.push('/zh/blog/guide');
                      }}
                    >
                      <Image
                        src="/blog/icon/ArrowLeft.svg"
                        className={classNames(styles.Customer_Svg, language === 'zh' ? '' : styles.Customer_Svg_EN)}
                        alt={''}
                        width={18}
                        height={16}
                      />
                    </div>
                  ),
                },
                {
                  title: (
                    <a
                      href="https://www.fwd.com.hk/"
                      target={'_blank'}
                      onClick={() => handleGA('首頁', 'https://www.fwd.com.hk/')}
                      className={styles.breadcrumb_item}
                    >
                      首頁
                    </a>
                  ),
                },
                {
                  title: (
                    <a
                      href="/zh/blog/"
                      onClick={() => handleGA('網誌', '/zh/blog/')}
                      className={styles.breadcrumb_item}
                    >
                      網誌
                    </a>
                  ),
                },
                {
                  title: (
                    <a
                      href="/zh/blog/guide/"
                      onClick={() => handleGA('超基本保險教室', '/zh/blog/guide/')}
                      className={styles.breadcrumb_item}
                    >
                      超基本保險教室
                    </a>
                  ),
                },
                // 不知道为什么，breadcrumb的最后一个样式是display：none，所以加多一个
                {
                  title: '',
                },
              ]}
            ></Breadcrumb>
          </ConfigProvider>
        </div>
        <div className={styles.content_wrapper}>
          <CollapseList data={initData?.table_of_content_reference[0]} />
          <div className={styles.intro_wrapper} id="intro_wrapper">
            <IntroductionPage data={initData} />
            {/* star 评分 */}
            <div className={styles.rate_wrapper}>
              <div className={styles.first_line_wrapper}>
                <p className={styles.rate_title}>{t('rating_title')}</p>
                <div className={styles.score_wrapper}>
                  <Image src="/blog/icon/Star.svg" className={styles.star} alt={''} width={25} height={25} />
                  <span className={styles.score_count}>{ratingScore.toFixed(1)}</span>
                </div>
              </div>
              <div className={styles.second_line_wrapper}>
                <p className={styles.rate_tip}>{isRate ? t('thanks_for_rate') : t('click_rate')}</p>
                {isRate ? (
                  <Image src="/blog/icon/Check.svg" alt={''} width={131} height={40} />
                ) : (
                  <Rating disabled={isRate} setRating={setRateForArticle} />
                )}
              </div>
            </div>

            {/* 刊登日期及分享文章等 */}
            <div className={styles.tip_wrapper}>
              <div className={styles.date_wrapper}>
                <p>
                  <span className={styles.date_title}>刊登日期 </span>
                  <span className={styles.date}>
                    : {dayJs(initData?.wiki_category_setup?.post_date).format(t('fomat_YYMMDD'))}
                  </span>
                </p>
                <p>
                  <span className={styles.date_title}>更新日期 </span>
                  <span className={styles.date}>
                    : {dayJs(initData?.wiki_category_setup?.last_update_date).format(t('fomat_YYMMDD'))}
                  </span>
                </p>
              </div>

              <div className={styles.share_wrapper}>
                <span className={styles.share_title}>分享文章</span>
                <a
                  href={shareMediaURl('facebook', shareUrl)}
                  target="_blank"
                  onClick={e => shareArticle(e, 'facebook')}
                  rel="noopener noreferrer"
                >
                  <Image
                    src="/blog/icon/FacebookDarkgreen.svg"
                    className={styles.share_icon}
                    alt={''}
                    width={25}
                    height={25}
                  />
                </a>
                <a
                  href={shareMediaURl('whatsapp', shareUrl)}
                  target="_blank"
                  onClick={e => shareArticle(e, 'whatsapp')}
                  rel="noopener noreferrer"
                >
                  <Image
                    src="/blog/icon/WhatsappDarkgreen.svg"
                    className={styles.share_icon}
                    alt={''}
                    width={25}
                    height={25}
                  />
                </a>
                <a
                  href={shareMediaURl('linkedin', shareUrl)}
                  target="_blank"
                  onClick={e => shareArticle(e, 'linkedin')}
                  rel="noopener noreferrer"
                >
                  <Image
                    src="/blog/icon/LinkedinDarkgreen.svg"
                    className={styles.share_icon}
                    alt={''}
                    width={23}
                    height={23}
                  />
                </a>
                <Image
                  src="/blog/icon/CopyDarkgreen.svg"
                  className={styles.share_icon}
                  alt={''}
                  width={25}
                  height={25}
                  onClick={() => copyLink()}
                />
              </div>
            </div>
          </div>
        </div>

        <Head>
          <>
            {breadcrumbList?.length > 0 ? (
              <script
                {...jsonLdScriptProps<BreadcrumbList>({
                  '@context': 'https://schema.org',
                  '@type': 'BreadcrumbList',
                  itemListElement: breadcrumbList?.map((item: any, index: number) => {
                    return {
                      '@type': 'ListItem',
                      position: index + 1,
                      name: item.name,
                      item: item.link,
                    };
                  }),
                })}
              />
            ) : (
              ''
            )}
          </>
        </Head>
      </div>
    </div>
  );
});
